import { useState, useEffect, useRef, useCallback, useMemo, memo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Plus, QrCode, Edit2, Trash2, Save, X, Upload, FileSpreadsheet, Image as ImageIcon, LogOut, Settings, Menu as MenuIcon, Megaphone, Calendar, Loader2, Copy } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ChangePassword from "./ChangePassword";
import SortableCategory from "@/components/SortableCategory";
import SortableProduct from "@/components/SortableProduct";
import InlineProductCard from "@/components/InlineProductCard";
import ProductForm from "@/components/ProductForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import AllergenSelector from "@/components/AllergenSelector";
import OptionsSelector from "@/components/OptionsSelector";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sortable Menu Item Component - Memoized para evitar re-renders innecesarios
const SortableMenuItem = memo(function SortableMenuItem({ menu, selectedMenu, setSelectedMenu, deleteMenu, currentUserRole, users, logoInputRef, handleLogoUpload, uploadingLogo, generateQR, openDuplicateDialog, createPresidenteForMenu, renameMenu, BACKEND_URL }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: menu.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [isEditingName, setIsEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(menu.name);

  useEffect(() => {
    setNameValue(menu.name);
  }, [menu.name]);

  const handleSaveName = async (e) => {
    if (e) e.stopPropagation();
    const trimmed = (nameValue || "").trim();
    if (!trimmed) {
      setNameValue(menu.name);
      setIsEditingName(false);
      return;
    }
    if (trimmed !== menu.name) {
      await renameMenu(menu.id, trimmed);
    }
    setIsEditingName(false);
  };

  const handleCancelName = (e) => {
    if (e) e.stopPropagation();
    setNameValue(menu.name);
    setIsEditingName(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      data-testid={`menu-card-${menu.id}`}
      className={`p-4 rounded-lg border-2 cursor-pointer transition ${
        selectedMenu?.id === menu.id
          ? "border-emerald-600 bg-emerald-50"
          : "border-gray-200 hover:border-emerald-300"
      }`}
      onClick={() => setSelectedMenu(menu)}
    >
      {/* Drag Handle */}
      {currentUserRole === 'admin' && (
        <div 
          {...listeners}
          className="flex justify-center mb-2 cursor-grab active:cursor-grabbing"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="w-8 h-1 bg-gray-300 rounded-full"></div>
        </div>
      )}

      {menu.logo_url && (
        <div className="mb-3 flex justify-center">
          <img 
            src={menu.logo_url.startsWith('http') ? menu.logo_url : `${BACKEND_URL}${menu.logo_url}`}
            alt={menu.name}
            className="h-16 w-auto object-contain"
          />
        </div>
      )}
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1 min-w-0">
          {isEditingName ? (
            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              <Input
                data-testid={`rename-menu-input-${menu.id}`}
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveName(e);
                  if (e.key === 'Escape') handleCancelName(e);
                }}
                autoFocus
                className="h-8 text-base font-semibold"
              />
              <Button
                data-testid={`save-menu-name-${menu.id}`}
                size="sm"
                variant="ghost"
                onClick={handleSaveName}
                className="text-emerald-600 hover:bg-emerald-50 px-2 h-8"
                title="Guardar nombre"
              >
                <Save className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                data-testid={`cancel-menu-name-${menu.id}`}
                onClick={handleCancelName}
                className="text-gray-500 hover:bg-gray-100 px-2 h-8"
                title="Cancelar"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <h3 className="font-semibold text-lg text-gray-900 truncate">{menu.name}</h3>
              <Button
                data-testid={`rename-menu-btn-${menu.id}`}
                size="sm"
                variant="ghost"
                onClick={(e) => { e.stopPropagation(); setIsEditingName(true); }}
                className="text-blue-600 hover:bg-blue-50 px-1.5 h-auto py-1 flex-shrink-0"
                title="Editar nombre de la carta"
              >
                <Edit2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
          {currentUserRole === 'admin' && menu.user_id && (
            <p className="text-xs text-blue-600 mt-1">
              👤 {users.find(u => u.id === menu.user_id)?.username || 'Usuario Owner'}
            </p>
          )}
          {currentUserRole === 'admin' && !menu.user_id && (
            <p className="text-xs text-red-600 mt-1">
              🔐 Admin
            </p>
          )}
        </div>
        <Button
          data-testid={`delete-menu-${menu.id}`}
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            deleteMenu(menu.id);
          }}
          className="text-red-500 hover:text-red-700 hover:bg-red-50"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      {selectedMenu?.id === menu.id && (
        <div className="space-y-2 mt-3">
          <input
            ref={logoInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleLogoUpload(e.target.files[0], menu.id)}
            className="hidden"
          />
          <Button
            data-testid="upload-logo-btn"
            onClick={(e) => {
              e.stopPropagation();
              logoInputRef.current?.click();
            }}
            disabled={uploadingLogo}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            size="sm"
          >
            <ImageIcon className="w-4 h-4 mr-2" />
            {uploadingLogo ? "Subiendo..." : menu.logo_url ? "Cambiar Logo" : "Añadir Logo"}
          </Button>
          <Button
            data-testid="show-qr-btn"
            onClick={(e) => {
              e.stopPropagation();
              generateQR();
            }}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
            size="sm"
          >
            <QrCode className="w-4 h-4 mr-2" />
            Mostrar QR
          </Button>
          {currentUserRole === 'admin' && (
            <Button
              data-testid="duplicate-menu-btn"
              onClick={(e) => {
                e.stopPropagation();
                openDuplicateDialog(menu);
              }}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              size="sm"
            >
              <Copy className="w-4 h-4 mr-2" />
              Duplicar Carta
            </Button>
          )}
          {(currentUserRole === 'admin' || currentUserRole === 'owner') && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                createPresidenteForMenu(menu.id);
              }}
              className="w-full bg-yellow-600 hover:bg-yellow-700 text-white"
              size="sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Crear Presidente
            </Button>
          )}
        </div>
      )}
    </div>
  );
});

export default function AdminPanel() {
  const navigate = useNavigate();
  const [menus, setMenus] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState({});
  const [showQR, setShowQR] = useState(false);
  const [qrImage, setQrImage] = useState("");
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const fileInputRef = useRef(null);
  const excelInputRef = useRef(null);
  const logoInputRef = useRef(null);
  
  // Form states
  const [newMenuName, setNewMenuName] = useState("");
  const [newCategoryName, setNewCategoryName] = useState("");
  const [editingMenuLogo, setEditingMenuLogo] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState("");
  
  // Loading states para evitar acciones duplicadas
  const [savingProduct, setSavingProduct] = useState(false);
  const [savingCategory, setSavingCategory] = useState(false);
  const [savingMenu, setSavingMenu] = useState(false);
  const [savingPromotion, setSavingPromotion] = useState(false);
  const [savingEvent, setSavingEvent] = useState(false);
  
  const [newProduct, setNewProduct] = useState({
    name: "",
    price: "",
    description: "",
    image_url: "",
    allergens: [],
    traces: [],
    options: []
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showProductForm, setShowProductForm] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editActiveTab, setEditActiveTab] = useState('allergens');
  const [inlineUploadingId, setInlineUploadingId] = useState(null);
  
  // Promotion states
  const [promotions, setPromotions] = useState([]);
  const [showPromotionDialog, setShowPromotionDialog] = useState(false);
  const [promotionForm, setPromotionForm] = useState({
    title: "",
    description: "",
    image_url: "",
    product_id: "",
    promotional_price: "",
    start_date: null,
    end_date: null
  });
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [uploadingPromotionImage, setUploadingPromotionImage] = useState(false);
  const promotionImageInputRef = useRef(null);

  // Event states
  const [events, setEvents] = useState([]);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: "",
    description: "",
    image_url: "",
    start_date: null,
    end_date: null
  });
  const [editingEvent, setEditingEvent] = useState(null);
  const [uploadingEventImage, setUploadingEventImage] = useState(false);
  const eventImageInputRef = useRef(null);

  // Duplicate menu states
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [menuToDuplicate, setMenuToDuplicate] = useState(null);
  const [duplicateMenuName, setDuplicateMenuName] = useState("");
  const [assignToUser, setAssignToUser] = useState("");

  // User management states
  const [users, setUsers] = useState([]);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [showUsersManagementDialog, setShowUsersManagementDialog] = useState(false);
  const [newUser, setNewUser] = useState({ username: "", password: "", role: "owner" });
  const [currentUserRole, setCurrentUserRole] = useState(null);

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("custom"); // custom, a-z, z-a, newest, oldest

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Configure axios to include auth token
  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    
    const loadInitialData = async () => {
      if (isMounted) {
        await fetchMenus();
        await getCurrentUserInfo();
      }
    };
    
    loadInitialData();
    
    // Cleanup: cancelar operaciones si el componente se desmonta
    return () => {
      isMounted = false;
    };
  }, []);

  const getCurrentUserInfo = async () => {
    try {
      const response = await axios.get(`${API}/users/me`);
      setCurrentUserRole(response.data.role);
      
      // If presidente, auto-select their menu
      if (response.data.role === 'presidente' && response.data.menu_id) {
        const menusResponse = await axios.get(`${API}/menus`);
        const presidenteMenu = menusResponse.data[0]; // Presidente only has access to one menu
        if (presidenteMenu) {
          setSelectedMenu(presidenteMenu);
          await fetchEvents(presidenteMenu.id);
        }
      }
      
      if (response.data.role === 'admin') {
        await fetchUsers();
      }
    } catch (error) {
      console.error("Error getting user info:", error);
    }
  };

  useEffect(() => {
    if (selectedMenu) {
      fetchCategories(selectedMenu.id);
      fetchPromotions(selectedMenu.id);
      fetchEvents(selectedMenu.id);
    }
  }, [selectedMenu]);

  const fetchMenus = async () => {
    try {
      const response = await axios.get(`${API}/menus`);
      setMenus(response.data);
      if (response.data.length > 0 && !selectedMenu) {
        setSelectedMenu(response.data[0]);
      }
    } catch (error) {
      console.error("Error fetching menus:", error);
      toast.error("Error al cargar las cartas");
    }
  };

  const fetchCategories = async (menuId) => {
    try {
      const response = await axios.get(`${API}/admin/menus/${menuId}/categories`);
      setCategories(response.data);
      
      const productsData = {};
      for (const category of response.data) {
        const productsResponse = await axios.get(`${API}/admin/categories/${category.id}/products`);
        productsData[category.id] = productsResponse.data;
      }
      setProducts(productsData);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Error al cargar las categorías");
    }
  };

  const createMenu = async () => {
    if (!newMenuName.trim()) {
      toast.error("Por favor ingresa un nombre para la carta");
      return;
    }
    try {
      const response = await axios.post(`${API}/menus`, { name: newMenuName });
      setMenus([...menus, response.data]);
      setSelectedMenu(response.data);
      setNewMenuName("");
      toast.success("Carta creada exitosamente");
    } catch (error) {
      console.error("Error creating menu:", error);
      toast.error("Error al crear la carta");
    }
  };

  const deleteMenu = useCallback(async (menuId) => {
    if (!window.confirm("¿Estás seguro de eliminar esta carta?")) return;
    try {
      await axios.delete(`${API}/menus/${menuId}`);
      const updatedMenus = menus.filter(m => m.id !== menuId);
      setMenus(updatedMenus);
      if (selectedMenu?.id === menuId) {
        setSelectedMenu(updatedMenus[0] || null);
      }
      toast.success("Carta eliminada exitosamente");
    } catch (error) {
      console.error("Error deleting menu:", error);
      toast.error("Error al eliminar la carta");
    }
  }, [menus, selectedMenu]);

  const openDuplicateDialog = useCallback((menu) => {
    setMenuToDuplicate(menu);
    setDuplicateMenuName(`Copia de ${menu.name}`);
    setAssignToUser("");
    setShowDuplicateDialog(true);
  }, []);

  const duplicateMenu = async () => {
    if (!duplicateMenuName.trim()) {
      toast.error("Por favor ingresa un nombre para la carta duplicada");
      return;
    }
    
    try {
      const payload = {
        new_name: duplicateMenuName
      };
      
      // If admin selected a user, assign the duplicated menu to that user
      if (currentUserRole === 'admin' && assignToUser) {
        payload.assign_to_user_id = assignToUser;
      }
      
      const response = await axios.post(`${API}/menus/${menuToDuplicate.id}/duplicate`, payload);
      toast.success(`Carta duplicada: ${response.data.new_menu_name}`);
      setShowDuplicateDialog(false);
      setMenuToDuplicate(null);
      setDuplicateMenuName("");
      setAssignToUser("");
      await fetchMenus();
      // Select the new duplicated menu
      setTimeout(() => {
        const newMenu = menus.find(m => m.id === response.data.new_menu_id);
        if (newMenu) {
          setSelectedMenu(newMenu);
        }
      }, 500);
    } catch (error) {
      console.error("Error duplicating menu:", error);
      toast.error("Error al duplicar la carta");
    }
  };

  // ========== USER MANAGEMENT FUNCTIONS ==========
  
  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const createUser = async () => {
    if (!newUser.username.trim() || !newUser.password.trim()) {
      toast.error("Usuario y contraseña son requeridos");
      return;
    }
    
    try {
      await axios.post(`${API}/users`, newUser);
      toast.success("Usuario creado exitosamente");
      setShowUserDialog(false);
      setNewUser({ username: "", password: "", role: "owner" });
      await fetchUsers();
    } catch (error) {
      console.error("Error creating user:", error);
      toast.error(error.response?.data?.detail || "Error al crear usuario");
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("¿Estás seguro de eliminar este usuario?")) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success("Usuario eliminado");
      await fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error("Error al eliminar usuario");
    }
  };

  const createPresidenteForMenu = useCallback(async (menuId) => {
    try {
      const response = await axios.post(`${API}/menus/${menuId}/presidente`);
      toast.success(`Presidente creado: ${response.data.username}`);
      alert(`CREDENCIALES PRESIDENTE:\nUsuario: ${response.data.username}\nContraseña: ${response.data.password}\n\nGuarda estas credenciales, no se volverán a mostrar.`);
      await fetchUsers();
    } catch (error) {
      console.error("Error creating presidente:", error);
      toast.error(error.response?.data?.detail || "Error al crear presidente");
    }
  }, []);

  const createCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error("Por favor ingresa un nombre para la familia");
      return;
    }
    if (savingCategory) return;
    
    setSavingCategory(true);
    try {
      const response = await axios.post(`${API}/categories`, {
        menu_id: selectedMenu.id,
        name: newCategoryName,
        order: categories.length
      });
      setCategories([...categories, response.data]);
      setNewCategoryName("");
      toast.success("Familia creada exitosamente");
    } catch (error) {
      console.error("Error creating category:", error);
      toast.error("Error al crear la familia");
    } finally {
      setSavingCategory(false);
    }
  };

  const updateCategory = async (categoryId) => {
    if (!editCategoryName.trim()) {
      toast.error("El nombre no puede estar vacío");
      return;
    }
    if (savingCategory) return;
    
    setSavingCategory(true);
    try {
      const response = await axios.put(`${API}/categories/${categoryId}`, {
        name: editCategoryName
      });
      setCategories(categories.map(c => c.id === categoryId ? response.data : c));
      setEditingCategory(null);
      setEditCategoryName("");
      toast.success("Familia actualizada exitosamente");
    } catch (error) {
      console.error("Error updating category:", error);
      toast.error("Error al actualizar la familia");
    } finally {
      setSavingCategory(false);
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!window.confirm("¿Estás seguro de eliminar esta familia?")) return;
    try {
      await axios.delete(`${API}/categories/${categoryId}`);
      setCategories(categories.filter(c => c.id !== categoryId));
      const newProducts = { ...products };
      delete newProducts[categoryId];
      setProducts(newProducts);
      toast.success("Familia eliminada exitosamente");
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.error("Error al eliminar la familia");
    }
  };

  const toggleCategory = async (categoryId) => {
    try {
      const response = await axios.put(`${API}/categories/${categoryId}/toggle`);
      setCategories(categories.map(c => 
        c.id === categoryId ? { ...c, active: response.data.active } : c
      ));
      toast.success(response.data.message);
    } catch (error) {
      console.error("Error toggling category:", error);
      toast.error("Error al cambiar estado de la familia");
    }
  };

  const toggleProduct = async (productId, categoryId) => {
    try {
      const response = await axios.put(`${API}/products/${productId}/toggle`);
      setProducts({
        ...products,
        [categoryId]: products[categoryId].map(p => 
          p.id === productId ? { ...p, active: response.data.active } : p
        )
      });
      toast.success(response.data.message);
    } catch (error) {
      console.error("Error toggling product:", error);
      toast.error("Error al cambiar estado del producto");
    }
  };

  const handleImageUpload = async (file, isEditing = false) => {
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Cloudinary returns full URL, use it directly
      const imageUrl = response.data.image_url;
      
      if (isEditing) {
        setEditingProduct({ ...editingProduct, image_url: imageUrl });
      } else {
        setNewProduct({ ...newProduct, image_url: imageUrl });
      }
      
      toast.success("Imagen subida exitosamente");
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Error al subir la imagen");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleLogoUpload = useCallback(async (file, menuId) => {
    if (!file) return;
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Cloudinary returns full URL, use it directly
      const logoUrl = response.data.logo_url;
      
      // Update menu with logo
      await axios.put(`${API}/menus/${menuId}`, { logo_url: logoUrl });
      
      // Refresh menus
      await fetchMenus();
      
      toast.success("Logo subido exitosamente");
      setEditingMenuLogo(null);
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error("Error al subir el logo");
    } finally {
      setUploadingLogo(false);
    }
  }, []);

  // Promotion functions
  const fetchPromotions = async (menuId) => {
    try {
      const response = await axios.get(`${API}/menus/${menuId}/promotions`);
      setPromotions(response.data);
    } catch (error) {
      console.error("Error fetching promotions:", error);
    }
  };

  const handlePromotionImageUpload = async (file) => {
    if (!file) return;
    
    setUploadingPromotionImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Cloudinary returns full URL, use it directly
      const imageUrl = response.data.image_url;
      setPromotionForm({ ...promotionForm, image_url: imageUrl });
      
      toast.success("Imagen subida exitosamente");
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Error al subir la imagen");
    } finally {
      setUploadingPromotionImage(false);
    }
  };

  const createOrUpdatePromotion = async () => {
    if (!promotionForm.title.trim()) {
      toast.error("El título es requerido");
      return;
    }
    if (savingPromotion) return;

    setSavingPromotion(true);
    try {
      const payload = {
        title: promotionForm.title,
        description: promotionForm.description,
        image_url: promotionForm.image_url,
        product_id: promotionForm.product_id,
        promotional_price: promotionForm.promotional_price ? parseFloat(promotionForm.promotional_price) : null,
        start_date: promotionForm.start_date ? new Date(promotionForm.start_date).toISOString() : null,
        end_date: promotionForm.end_date ? new Date(promotionForm.end_date).toISOString() : null
      };

      if (editingPromotion) {
        await axios.put(`${API}/promotions/${editingPromotion.id}`, payload);
        toast.success("Promoción actualizada exitosamente");
      } else {
        await axios.post(`${API}/promotions`, {
          menu_id: selectedMenu.id,
          ...payload
        });
        toast.success("Promoción creada exitosamente");
      }
      
      setShowPromotionDialog(false);
      setPromotionForm({ title: "", description: "", image_url: "", product_id: "", promotional_price: "", start_date: null, end_date: null });
      setEditingPromotion(null);
      await fetchPromotions(selectedMenu.id);
    } catch (error) {
      console.error("Error saving promotion:", error);
      toast.error("Error al guardar la promoción");
    } finally {
      setSavingPromotion(false);
    }
  };

  const togglePromotion = async (promotionId) => {
    try {
      const response = await axios.put(`${API}/promotions/${promotionId}/toggle`);
      toast.success(response.data.message);
      await fetchPromotions(selectedMenu.id);
    } catch (error) {
      console.error("Error toggling promotion:", error);
      toast.error("Error al cambiar estado de la promoción");
    }
  };

  const deletePromotion = async (promotionId) => {
    if (!window.confirm("¿Estás seguro de eliminar esta promoción?")) return;
    try {
      await axios.delete(`${API}/promotions/${promotionId}`);
      toast.success("Promoción eliminada exitosamente");
      await fetchPromotions(selectedMenu.id);
    } catch (error) {
      console.error("Error deleting promotion:", error);
      toast.error("Error al eliminar la promoción");
    }
  };

  const openEditPromotion = (promotion) => {
    setEditingPromotion(promotion);
    setPromotionForm({
      title: promotion.title,
      description: promotion.description,
      image_url: promotion.image_url || "",
      product_id: promotion.product_id || "",
      promotional_price: promotion.promotional_price || "",
      start_date: promotion.start_date ? new Date(promotion.start_date) : null,
      end_date: promotion.end_date ? new Date(promotion.end_date) : null
    });
    setShowPromotionDialog(true);
  };

  const handleProductSelect = (productId) => {
    setPromotionForm({ ...promotionForm, product_id: productId });
    
    // Auto-load product image
    if (productId) {
      for (const category of categories) {
        const product = (products[category.id] || []).find(p => p.id === productId);
        if (product) {
          setPromotionForm(prev => ({
            ...prev,
            product_id: productId,
            image_url: product.image_url || prev.image_url
          }));
          break;
        }
      }
    }
  };

  // ========== EVENT FUNCTIONS ==========
  
  const fetchEvents = async (menuId) => {
    try {
      const response = await axios.get(`${API}/menus/${menuId}/events/all`);
      setEvents(response.data);
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  };

  const handleEventImageUpload = async (file) => {
    if (!file) return;
    
    setUploadingEventImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Cloudinary returns full URL, use it directly
      const imageUrl = response.data.image_url;
      setEventForm({ ...eventForm, image_url: imageUrl });
      
      toast.success("Imagen subida exitosamente");
    } catch (error) {
      console.error("Error uploading event image:", error);
      toast.error("Error al subir la imagen");
    } finally {
      setUploadingEventImage(false);
    }
  };

  const saveEvent = async () => {
    if (!eventForm.title.trim()) {
      toast.error("El título es requerido");
      return;
    }
    if (savingEvent) return;
    
    setSavingEvent(true);
    try {
      const eventData = {
        menu_id: selectedMenu.id,
        title: eventForm.title,
        description: eventForm.description,
        image_url: eventForm.image_url,
        start_date: eventForm.start_date ? eventForm.start_date.toISOString() : null,
        end_date: eventForm.end_date ? eventForm.end_date.toISOString() : null
      };

      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, eventData);
        toast.success("Evento actualizado exitosamente");
      } else {
        await axios.post(`${API}/events`, eventData);
        toast.success("Evento creado exitosamente");
      }
      
      setShowEventDialog(false);
      setEditingEvent(null);
      setEventForm({
        title: "",
        description: "",
        image_url: "",
        start_date: null,
        end_date: null
      });
      await fetchEvents(selectedMenu.id);
    } catch (error) {
      console.error("Error saving event:", error);
      toast.error("Error al guardar el evento");
    } finally {
      setSavingEvent(false);
    }
  };

  const toggleEvent = async (eventId) => {
    try {
      await axios.put(`${API}/events/${eventId}/toggle`);
      await fetchEvents(selectedMenu.id);
    } catch (error) {
      console.error("Error toggling event:", error);
      toast.error("Error al cambiar estado del evento");
    }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm("¿Estás seguro de eliminar este evento?")) return;
    try {
      await axios.delete(`${API}/events/${eventId}`);
      toast.success("Evento eliminado exitosamente");
      await fetchEvents(selectedMenu.id);
    } catch (error) {
      console.error("Error deleting event:", error);
      toast.error("Error al eliminar el evento");
    }
  };

  const openEditEvent = (event) => {
    setEditingEvent(event);
    setEventForm({
      title: event.title,
      description: event.description,
      image_url: event.image_url || "",
      start_date: event.start_date ? new Date(event.start_date) : null,
      end_date: event.end_date ? new Date(event.end_date) : null
    });
    setShowEventDialog(true);
  };

  const createProduct = async (categoryId) => {
    if (!newProduct.name.trim() || newProduct.price === "" || newProduct.price === null) {
      toast.error("Por favor completa los campos requeridos");
      return;
    }
    if (savingProduct) return; // Evitar guardados duplicados
    
    setSavingProduct(true);
    try {
      const response = await axios.post(`${API}/products`, {
        category_id: categoryId,
        name: newProduct.name,
        price: parseFloat(newProduct.price),
        description: newProduct.description,
        image_url: newProduct.image_url,
        allergens: newProduct.allergens,
        traces: newProduct.traces,
        options: newProduct.options,
        order: (products[categoryId] || []).length
      });
      setProducts({
        ...products,
        [categoryId]: [...(products[categoryId] || []), response.data]
      });
      setNewProduct({ name: "", price: "", description: "", image_url: "", allergens: [], traces: [], options: [] });
      setShowProductForm(null);
      toast.success("Producto creado exitosamente");
    } catch (error) {
      console.error("Error creating product:", error);
      toast.error("Error al crear el producto");
    } finally {
      setSavingProduct(false);
    }
  };

  const updateProduct = async (productId, categoryId) => {
    if (!editingProduct.name.trim() || editingProduct.price === "" || editingProduct.price === null) {
      toast.error("Por favor completa los campos requeridos");
      return;
    }
    if (savingProduct) return; // Evitar guardados duplicados
    
    setSavingProduct(true);
    try {
      const response = await axios.put(`${API}/products/${productId}`, {
        name: editingProduct.name,
        price: parseFloat(editingProduct.price),
        description: editingProduct.description,
        image_url: editingProduct.image_url,
        allergens: editingProduct.allergens,
        traces: editingProduct.traces,
        options: editingProduct.options
      });
      setProducts({
        ...products,
        [categoryId]: products[categoryId].map(p => p.id === productId ? response.data : p)
      });
      setEditingProduct(null);
      setEditActiveTab('allergens');
      toast.success("Producto actualizado exitosamente");
    } catch (error) {
      console.error("Error updating product:", error);
      toast.error("Error al actualizar el producto");
    } finally {
      setSavingProduct(false);
    }
  };

  const saveProductFields = async (product, categoryId, updates) => {
    const newName = updates.name !== undefined ? updates.name : product.name;
    const newPriceRaw = updates.price !== undefined ? updates.price : product.price;
    const newPrice = parseFloat(newPriceRaw);
    if (!newName || !newName.trim()) {
      toast.error("El nombre no puede estar vacío");
      return false;
    }
    if (isNaN(newPrice) || newPrice < 0) {
      toast.error("Precio inválido");
      return false;
    }
    try {
      const response = await axios.put(`${API}/products/${product.id}`, {
        name: newName,
        price: newPrice,
        description: updates.description !== undefined ? updates.description : product.description,
        image_url: updates.image_url !== undefined ? updates.image_url : product.image_url,
        allergens: product.allergens || [],
        traces: product.traces || [],
        options: product.options || []
      });
      setProducts(prev => ({
        ...prev,
        [categoryId]: prev[categoryId].map(p => p.id === product.id ? response.data : p)
      }));
      toast.success("Guardado");
      return true;
    } catch (error) {
      console.error("Error saving product fields:", error);
      toast.error("Error al guardar");
      return false;
    }
  };

  const handleInlineImageUpload = async (file, product, categoryId) => {
    if (!file) return;
    setInlineUploadingId(product.id);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await saveProductFields(product, categoryId, { image_url: response.data.image_url });
    } catch (error) {
      console.error("Error uploading inline image:", error);
      toast.error("Error al subir la imagen");
    } finally {
      setInlineUploadingId(null);
    }
  };


  const deleteProduct = async (productId, categoryId) => {
    if (!window.confirm("¿Estás seguro de eliminar este producto?")) return;
    try {
      await axios.delete(`${API}/products/${productId}`);
      setProducts({
        ...products,
        [categoryId]: products[categoryId].filter(p => p.id !== productId)
      });
      toast.success("Producto eliminado exitosamente");
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error("Error al eliminar el producto");
    }
  };

  const handleExcelImport = async (file) => {
    if (!file) return;
    if (!selectedMenu) {
      toast.error("Por favor selecciona una carta primero");
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/import-excel/${selectedMenu.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Importación exitosa: ${response.data.categories} categorías, ${response.data.products} productos`);
      setShowImportDialog(false);
      await fetchCategories(selectedMenu.id);
    } catch (error) {
      console.error("Error importing Excel:", error);
      toast.error("Error al importar el archivo Excel");
    }
  };

  const generateQR = useCallback(async () => {
    if (!selectedMenu) return;
    try {
      const response = await axios.get(`${API}/menus/${selectedMenu.id}/qr`, {
        responseType: 'blob'
      });
      const imageUrl = URL.createObjectURL(response.data);
      setQrImage(imageUrl);
      setShowQR(true);
    } catch (error) {
      console.error("Error generating QR:", error);
      toast.error("Error al generar el código QR");
    }
  }, [selectedMenu]);

  const renameMenu = useCallback(async (menuId, newName) => {
    try {
      const response = await axios.put(`${API}/menus/${menuId}`, { name: newName });
      setMenus(prev => prev.map(m => m.id === menuId ? { ...m, name: response.data.name } : m));
      setSelectedMenu(prev => (prev && prev.id === menuId ? { ...prev, name: response.data.name } : prev));
      toast.success("Nombre de la carta actualizado");
    } catch (error) {
      console.error("Error renaming menu:", error);
      toast.error("Error al cambiar el nombre");
    }
  }, []);

  const [exportingExcel, setExportingExcel] = useState(false);
  const handleExcelExport = async () => {
    if (!selectedMenu || exportingExcel) return;
    setExportingExcel(true);
    try {
      const response = await axios.get(`${API}/menus/${selectedMenu.id}/export-excel`, {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const safeName = (selectedMenu.name || 'carta').replace(/[^a-zA-Z0-9 _-]/g, '').trim() || 'carta';
      link.download = `${safeName}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Carta exportada a Excel");
    } catch (error) {
      console.error("Error exporting Excel:", error);
      toast.error("Error al exportar la carta");
    } finally {
      setExportingExcel(false);
    }
  };

  // Filtrar y ordenar menús
  const filteredAndSortedMenus = useMemo(() => {
    let filtered = [...menus];

    // Aplicar búsqueda
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(menu => {
        const menuName = menu.name.toLowerCase();
        const ownerName = users.find(u => u.id === menu.user_id)?.username?.toLowerCase() || "";
        return menuName.includes(query) || ownerName.includes(query);
      });
    }

    // Aplicar ordenamiento
    switch (sortOrder) {
      case "a-z":
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "z-a":
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case "newest":
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case "oldest":
        filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        break;
      case "custom":
      default:
        filtered.sort((a, b) => a.order - b.order);
        break;
    }

    return filtered;
  }, [menus, searchQuery, sortOrder, users]);

  // Memoizar IDs de menús para evitar recalcular en cada render
  // Usar filteredAndSortedMenus solo cuando hay búsqueda/ordenamiento activo
  const menuIds = useMemo(() => {
    return (searchQuery || sortOrder !== 'custom') 
      ? filteredAndSortedMenus.map(m => m.id)
      : menus.map(m => m.id);
  }, [menus, filteredAndSortedMenus, searchQuery, sortOrder]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    delete axios.defaults.headers.common['Authorization'];
    toast.success("Sesión cerrada");
    navigate("/login");
  };

  const handleDragEndMenus = async (event) => {
    const {active, over} = event;
    
    if (!over || active.id === over.id) return;
    
    const oldIndex = menus.findIndex(menu => menu.id === active.id);
    const newIndex = menus.findIndex(menu => menu.id === over.id);
    
    const newMenus = arrayMove(menus, oldIndex, newIndex);
    setMenus(newMenus);
    
    // Save new order to backend
    try {
      const menuIds = newMenus.map(m => m.id);
      await axios.post(`${API}/menus/reorder`, menuIds);
      toast.success("Orden actualizado");
    } catch (error) {
      console.error("Error reordering menus:", error);
      toast.error("Error al actualizar orden");
      // Revert on error
      await fetchMenus();
    }
  };

  // Handle drag end for categories
  const handleDragEndCategories = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      const oldIndex = categories.findIndex((cat) => cat.id === active.id);
      const newIndex = categories.findIndex((cat) => cat.id === over.id);
      
      const newCategories = arrayMove(categories, oldIndex, newIndex);
      setCategories(newCategories);
      
      try {
        for (let i = 0; i < newCategories.length; i++) {
          await axios.put(`${API}/categories/${newCategories[i].id}`, {
            order: i
          });
        }
        toast.success("Orden actualizado");
      } catch (error) {
        console.error("Error updating order:", error);
        toast.error("Error al actualizar el orden");
      }
    }
  };

  // Handle drag end for products
  const handleDragEndProducts = async (event, categoryId) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      const categoryProducts = products[categoryId];
      const oldIndex = categoryProducts.findIndex((prod) => prod.id === active.id);
      const newIndex = categoryProducts.findIndex((prod) => prod.id === over.id);
      
      const newProducts = arrayMove(categoryProducts, oldIndex, newIndex);
      setProducts({
        ...products,
        [categoryId]: newProducts
      });
      
      try {
        for (let i = 0; i < newProducts.length; i++) {
          await axios.put(`${API}/products/${newProducts[i].id}`, {
            order: i
          });
        }
        toast.success("Orden actualizado");
      } catch (error) {
        console.error("Error updating order:", error);
        toast.error("Error al actualizar el orden");
      }
    }
  };

  // Auto scroll when opening accordion
  const handleAccordionChange = (categoryId) => {
    if (categoryId) {
      setTimeout(() => {
        const element = document.querySelector(`[data-category-id="${categoryId}"]`);
        if (element) {
          const yOffset = -100; // Offset desde la parte superior
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 300); // Aumentado el delay para asegurar que el accordion esté expandido
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
      {/* Collapsible Sidebar */}
      <div className={`fixed top-0 left-0 h-full bg-white border-r border-gray-200 transition-all duration-300 z-50 ${sidebarOpen ? 'w-64' : 'w-0'}`}>
        {sidebarOpen && (
          <div className="p-6 h-full flex flex-col">
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Admin Panel</h2>
              <p className="text-sm text-gray-500">Gestiona tu restaurante</p>
            </div>
            
            <div className="flex-1" />
            
            <div className="space-y-2">
              {currentUserRole === 'admin' && (
                <button
                  onClick={() => {
                    setShowUsersManagementDialog(true);
                    setSidebarOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-blue-600 hover:bg-blue-50 transition"
                >
                  <Plus className="w-5 h-5" />
                  Gestionar Usuarios
                </button>
              )}
              
              {currentUserRole === 'admin' && (
                <button
                  onClick={() => setShowChangePassword(true)}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                >
                  <Settings className="w-5 h-5" />
                  Cambiar Contraseña
                </button>
              )}
              
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition"
              >
                <LogOut className="w-5 h-5" />
                Cerrar Sesión
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Hamburger Menu Button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="fixed top-4 left-4 z-50 p-3 bg-white rounded-lg shadow-md hover:bg-gray-50 transition"
      >
        <MenuIcon className="w-6 h-6 text-gray-700" />
      </button>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-0'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-20">
          
          {/* PRESIDENTE VIEW - Only Events Management */}
          {currentUserRole === 'presidente' ? (
            <>
              {/* Presidente Header */}
              <div className="mb-8">
                <h1 className="text-4xl sm:text-5xl font-bold text-yellow-600 mb-2">
                  Panel Presidente 👑
                </h1>
                <p className="text-lg text-gray-600">Gestiona eventos de la carta: {selectedMenu?.name || 'Cargando...'}</p>
              </div>

              {/* Events Management Only */}
              {selectedMenu && (
                <div className="card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-2xl font-semibold text-gray-900">Eventos y Conciertos 💃</h2>
                      <p className="text-sm text-gray-600">Gestiona eventos especiales de esta carta</p>
                    </div>
                    <Button
                      onClick={() => {
                        setEventForm({ title: "", description: "", image_url: "", start_date: null, end_date: null });
                        setEditingEvent(null);
                        setShowEventDialog(true);
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Nuevo Evento
                    </Button>
                  </div>

                  {events.length > 0 ? (
                    <div className="space-y-3">
                      {events.map((event) => (
                        <div key={event.id} className={`p-4 rounded-lg border-2 ${event.active ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                          <div className="flex gap-4">
                            {event.image_url && (
                              <img
                                src={event.image_url.startsWith('http') ? event.image_url : `${BACKEND_URL}${event.image_url}`}
                                alt={event.title}
                                className="w-20 h-20 object-cover rounded-lg"
                              />
                            )}
                            <div className="flex-1">
                              <div className="flex justify-between items-start">
                                <div>
                                  <h3 className="font-semibold text-lg text-gray-900">{event.title}</h3>
                                  <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                                    <span className={`text-xs px-2 py-1 rounded-full ${event.active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                                      {event.active ? '✓ Activo' : 'Inactivo'}
                                    </span>
                                    {event.start_date && (
                                      <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                                        📅 {new Date(event.start_date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                      </span>
                                    )}
                                    {event.end_date && (
                                      <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700">
                                        ⏰ {new Date(event.end_date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => toggleEvent(event.id)}
                                    className={event.active ? 'text-gray-600 hover:text-gray-700' : 'text-green-600 hover:text-green-700'}
                                  >
                                    {event.active ? 'Desactivar' : 'Activar'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => openEditEvent(event)}
                                    className="text-blue-600 hover:text-blue-700"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => deleteEvent(event.id)}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <div className="text-4xl mb-3">💃</div>
                      <p>No hay eventos creados. Crea uno para anunciar conciertos, festividades, etc.</p>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              {/* ADMIN/OWNER VIEW - Full Panel */}
              {/* Header */}
              <div className="mb-8">
                <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-2" data-testid="admin-title">
                  Panel de Administración
                </h1>
                <p className="text-lg text-gray-600">Gestiona tus cartas de restaurante</p>
              </div>

              {/* Create Menu Section - Only for Admin */}
              {currentUserRole === 'admin' && (
                <div className="card p-6 mb-8" data-testid="create-menu-section">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-semibold text-gray-900">Crear Nueva Carta</h2>
                    {selectedMenu && (
                      <Button
                        data-testid="import-excel-btn"
                        onClick={() => setShowImportDialog(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Importar Excel
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <Input
                      data-testid="new-menu-input"
                      placeholder="Nombre de la carta (ej: Carta Verano 2025)"
                      value={newMenuName}
                      onChange={(e) => setNewMenuName(e.target.value)}
                      className="flex-1"
                    />
                    <Button 
                      data-testid="create-menu-btn"
                      onClick={createMenu}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white px-6"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Crear Carta
                    </Button>
                  </div>
                </div>
              )}

          {/* Menu Selection */}
          {menus.length > 0 && (
            <div className="card p-6 mb-8" data-testid="menu-list-section">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900">
                    Mis Cartas
                    {currentUserRole === 'admin' && sortOrder === 'custom' && !searchQuery && (
                      <span className="text-sm text-gray-500 ml-2">(Arrastra para reordenar)</span>
                    )}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {searchQuery || sortOrder !== 'custom' 
                      ? `Mostrando ${filteredAndSortedMenus.length} de ${menus.length} cartas`
                      : `${menus.length} ${menus.length === 1 ? 'carta' : 'cartas'} en total`
                    }
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                  {/* Buscador */}
                  <div className="relative flex-1 sm:w-64">
                    <input
                      type="text"
                      placeholder="Buscar carta..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                    />
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>

                  {/* Ordenamiento */}
                  <select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
                  >
                    <option value="custom">🎯 Orden personalizado</option>
                    <option value="a-z">🔤 A → Z</option>
                    <option value="z-a">🔤 Z → A</option>
                    <option value="newest">📅 Más recientes</option>
                    <option value="oldest">📅 Más antiguas</option>
                  </select>
                </div>
              </div>

              {filteredAndSortedMenus.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <p className="text-gray-500 text-lg">No se encontraron cartas que coincidan con "{searchQuery}"</p>
                  <button
                    onClick={() => setSearchQuery("")}
                    className="mt-4 text-emerald-600 hover:text-emerald-700 font-medium"
                  >
                    Limpiar búsqueda
                  </button>
                </div>
              ) : (
                <DndContext 
                  sensors={sensors} 
                  collisionDetection={closestCenter} 
                  onDragEnd={handleDragEndMenus}
                >
                  <SortableContext items={menuIds} strategy={rectSortingStrategy}>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {filteredAndSortedMenus.map((menu) => (
                        <SortableMenuItem
                          key={menu.id}
                          menu={menu}
                          selectedMenu={selectedMenu}
                          setSelectedMenu={setSelectedMenu}
                          deleteMenu={deleteMenu}
                        currentUserRole={currentUserRole}
                        users={users}
                        logoInputRef={logoInputRef}
                        handleLogoUpload={handleLogoUpload}
                        uploadingLogo={uploadingLogo}
                        generateQR={generateQR}
                        openDuplicateDialog={openDuplicateDialog}
                        createPresidenteForMenu={createPresidenteForMenu}
                        renameMenu={renameMenu}
                        BACKEND_URL={BACKEND_URL}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              )}
            </div>
          )}

          {/* Promotion Management */}
          {selectedMenu && (
            <div className="card p-6 mb-8" data-testid="promotion-section">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900">Promoción Destacada</h2>
                  <p className="text-sm text-gray-600">Aparece al abrir el menú público</p>
                </div>
                <Button
                  onClick={() => {
                    setPromotionForm({ title: "", description: "", image_url: "", product_id: "", promotional_price: "", start_date: null, end_date: null });
                    setEditingPromotion(null);
                    setShowPromotionDialog(true);
                  }}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                >
                  <Megaphone className="w-4 h-4 mr-2" />
                  Nueva Promoción
                </Button>
              </div>

              {promotions.length > 0 ? (
                <div className="space-y-3">
                  {promotions.map((promo) => (
                    <div key={promo.id} className={`p-4 rounded-lg border-2 ${promo.active ? 'border-orange-500 bg-orange-50' : 'border-gray-200 bg-gray-50'}`}>
                      <div className="flex gap-4">
                        {promo.image_url && (
                          <img
                            src={promo.image_url.startsWith('http') ? promo.image_url : `${BACKEND_URL}${promo.image_url}`}
                            alt={promo.title}
                            className="w-20 h-20 object-cover rounded-lg"
                          />
                        )}
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="font-semibold text-lg text-gray-900">{promo.title}</h3>
                              <p className="text-sm text-gray-600 mt-1">{promo.description}</p>
                              <div className="flex items-center gap-2 mt-2 flex-wrap">
                                <span className={`text-xs px-2 py-1 rounded-full ${promo.active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                                  {promo.active ? '✓ Activa' : 'Inactiva'}
                                </span>
                                {promo.start_date && (
                                  <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                                    📅 Desde: {new Date(promo.start_date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                )}
                                {promo.end_date && (
                                  <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700">
                                    ⏰ Hasta: {new Date(promo.end_date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => togglePromotion(promo.id)}
                                className={promo.active ? 'text-gray-600 hover:text-gray-700' : 'text-green-600 hover:text-green-700'}
                              >
                                {promo.active ? 'Desactivar' : 'Activar'}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEditPromotion(promo)}
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => deletePromotion(promo.id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Megaphone className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No hay promociones creadas. Crea una para destacar platos especiales.</p>
                </div>
              )}
            </div>
          )}

          {/* Events Management */}
          {selectedMenu && (
            <div className="card p-6" data-testid="events-section">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900">Eventos y Conciertos 💃</h2>
                  <p className="text-sm text-gray-600">Gestiona eventos especiales, solo visibles al pulsar botón verde</p>
                </div>
                <Button
                  onClick={() => {
                    setEventForm({ title: "", description: "", image_url: "", start_date: null, end_date: null });
                    setEditingEvent(null);
                    setShowEventDialog(true);
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Evento
                </Button>
              </div>

              {events.length > 0 ? (
                <div className="space-y-3">
                  {events.map((event) => (
                    <div key={event.id} className={`p-4 rounded-lg border-2 ${event.active ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                      <div className="flex gap-4">
                        {event.image_url && (
                          <img
                            src={event.image_url.startsWith('http') ? event.image_url : `${BACKEND_URL}${event.image_url}`}
                            alt={event.title}
                            className="w-20 h-20 object-cover rounded-lg"
                          />
                        )}
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="font-semibold text-lg text-gray-900">{event.title}</h3>
                              <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                              <div className="flex items-center gap-2 mt-2 flex-wrap">
                                <span className={`text-xs px-2 py-1 rounded-full ${event.active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                                  {event.active ? '✓ Activo' : 'Inactivo'}
                                </span>
                                {event.start_date && (
                                  <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                                    📅 Desde: {new Date(event.start_date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                )}
                                {event.end_date && (
                                  <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700">
                                    ⏰ Hasta: {new Date(event.end_date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => toggleEvent(event.id)}
                                className={event.active ? 'text-gray-600 hover:text-gray-700' : 'text-green-600 hover:text-green-700'}
                              >
                                {event.active ? 'Desactivar' : 'Activar'}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEditEvent(event)}
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => deleteEvent(event.id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-3">💃</div>
                  <p>No hay eventos creados. Crea uno para anunciar conciertos, festividades, etc.</p>
                </div>
              )}
            </div>
          )}

          {/* Categories and Products Management */}
          {selectedMenu && (
            <div className="card p-6" data-testid="categories-section">
              <div className="mb-4">
                <div className="flex justify-between items-center mb-3">
                  <h2 className="text-2xl font-semibold text-gray-900">
                    Gestionar: {selectedMenu.name}
                  </h2>
                  <Button
                    data-testid="export-excel-btn"
                    onClick={handleExcelExport}
                    disabled={exportingExcel}
                    variant="outline"
                    className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                  >
                    {exportingExcel ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Exportando...
                      </>
                    ) : (
                      <>
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Exportar Excel
                      </>
                    )}
                  </Button>
                </div>
                
                {/* Toggles Container */}
                <div className="flex flex-wrap gap-3">
                  {/* Cart Total Toggle */}
                  <div className="flex items-center gap-3 bg-gray-50 px-4 py-2 rounded-lg">
                    <span className="text-sm text-gray-700">Mostrar total del carrito:</span>
                    <button
                      onClick={async () => {
                        try {
                          const response = await axios.put(`${API}/menus/${selectedMenu.id}/toggle-cart-total`);
                          setSelectedMenu({...selectedMenu, show_cart_total: response.data.show_cart_total});
                          setMenus(menus.map(m => m.id === selectedMenu.id ? {...m, show_cart_total: response.data.show_cart_total} : m));
                          toast.success(response.data.show_cart_total ? "Total activado" : "Total desactivado");
                        } catch (error) {
                          console.error("Error toggling cart total:", error);
                          toast.error("Error al cambiar configuración");
                        }
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                        selectedMenu.show_cart_total !== false ? 'bg-emerald-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                          selectedMenu.show_cart_total !== false ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span className="text-xs text-gray-500">
                      {selectedMenu.show_cart_total !== false ? '✓ Visible' : '✗ Oculto'}
                    </span>
                  </div>

                  {/* Events Button Toggle */}
                  <div className="flex items-center gap-3 bg-gray-50 px-4 py-2 rounded-lg">
                    <span className="text-sm text-gray-700">Botón de eventos:</span>
                    <button
                      onClick={async () => {
                        try {
                          const response = await axios.put(`${API}/menus/${selectedMenu.id}/toggle-events-button`);
                          setSelectedMenu({...selectedMenu, show_events_button: response.data.show_events_button});
                          setMenus(menus.map(m => m.id === selectedMenu.id ? {...m, show_events_button: response.data.show_events_button} : m));
                          toast.success(response.data.show_events_button ? "Botón de eventos activado" : "Botón de eventos desactivado");
                        } catch (error) {
                          console.error("Error toggling events button:", error);
                          toast.error("Error al cambiar configuración");
                        }
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                        selectedMenu.show_events_button !== false ? 'bg-emerald-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                          selectedMenu.show_events_button !== false ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span className="text-xs text-gray-500">
                      {selectedMenu.show_events_button !== false ? '✓ Visible' : '✗ Oculto'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Add Category */}
              <div className="mb-6 p-4 bg-emerald-50 rounded-lg" data-testid="add-category-section">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Nueva Familia de Productos</h3>
                <div className="flex gap-3">
                  <Input
                    data-testid="new-category-input"
                    placeholder="Nombre de la familia (ej: BEBIDAS, ENTRANTES)"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    data-testid="create-category-btn"
                    onClick={createCategory}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Añadir Familia
                  </Button>
                </div>
              </div>

              {/* Categories List */}
              {categories.length > 0 ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEndCategories}
                >
                  <SortableContext
                    items={categories.map(c => c.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <Accordion type="single" collapsible className="space-y-4" onValueChange={handleAccordionChange}>
                      {categories.map((category) => (
                        <SortableCategory key={category.id} category={category}>
                          <AccordionItem value={category.id} className="border rounded-lg px-4">
                            <div className="flex items-center justify-between w-full py-4">
                              {editingCategory === category.id ? (
                                <div className="flex items-center gap-2 flex-1">
                                  <Input
                                    data-testid={`edit-category-input-${category.id}`}
                                    value={editCategoryName}
                                    onChange={(e) => setEditCategoryName(e.target.value)}
                                    className="w-64"
                                  />
                                  <Button
                                    data-testid={`save-category-${category.id}`}
                                    size="sm"
                                    onClick={() => updateCategory(category.id)}
                                    className="bg-emerald-600 hover:bg-emerald-700"
                                  >
                                    <Save className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    data-testid={`cancel-edit-category-${category.id}`}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      setEditingCategory(null);
                                      setEditCategoryName("");
                                    }}
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              ) : (
                                <>
                                  <AccordionTrigger data-testid={`category-trigger-${category.id}`} className="hover:no-underline flex-1">
                                    <div className="flex items-center gap-3">
                                      <span className={`text-lg font-semibold ${category.active === false ? 'text-gray-400' : 'text-gray-900'}`}>
                                        {category.name}
                                        {category.active === false && <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">Inactiva</span>}
                                      </span>
                                      <span className="text-sm text-gray-500">({(products[category.id] || []).length} productos)</span>
                                    </div>
                                  </AccordionTrigger>
                                  <div className="flex gap-2 ml-4">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => toggleCategory(category.id)}
                                      className={category.active === false ? 'text-green-600 hover:text-green-700 hover:bg-green-50' : 'text-gray-600 hover:text-gray-700 hover:bg-gray-50'}
                                      title={category.active === false ? 'Activar familia' : 'Desactivar familia'}
                                    >
                                      {category.active === false ? 'Activar' : 'Desactivar'}
                                    </Button>
                                    <Button
                                      data-testid={`edit-category-btn-${category.id}`}
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        setEditingCategory(category.id);
                                        setEditCategoryName(category.name);
                                      }}
                                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      data-testid={`delete-category-${category.id}`}
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => deleteCategory(category.id)}
                                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </>
                              )}
                            </div>
                            <AccordionContent>
                              <div className="pt-4 pb-2">
                                {/* Add Product Form */}
                                {showProductForm === category.id ? (
                                  <div data-testid={`add-product-form-${category.id}`}>
                                    <ProductForm
                                      productData={newProduct}
                                      onProductChange={setNewProduct}
                                      onSave={() => createProduct(category.id)}
                                      onCancel={() => {
                                        setShowProductForm(null);
                                        setNewProduct({ name: "", price: "", description: "", image_url: "", allergens: [], traces: [], options: [] });
                                      }}
                                      onImageUpload={(file) => handleImageUpload(file, false)}
                                      uploadingImage={uploadingImage}
                                      saving={savingProduct}
                                    />
                                  </div>
                                ) : (
                                  <Button
                                    data-testid={`add-product-btn-${category.id}`}
                                    onClick={() => setShowProductForm(category.id)}
                                    className="mb-4 bg-emerald-600 hover:bg-emerald-700 text-white"
                                    size="sm"
                                  >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Añadir Producto
                                  </Button>
                                )}

                                {/* Products List */}
                                <DndContext
                                  sensors={sensors}
                                  collisionDetection={closestCenter}
                                  onDragEnd={(event) => handleDragEndProducts(event, category.id)}
                                >
                                  <SortableContext
                                    items={(products[category.id] || []).map(p => p.id)}
                                    strategy={verticalListSortingStrategy}
                                  >
                                    <div className="space-y-3">
                                      {(products[category.id] || []).map((product) => (
                                        <SortableProduct key={product.id} product={product}>
                                          {editingProduct?.id === product.id ? (
                                            <div className="p-6 border rounded-lg bg-blue-50" data-testid={`edit-product-form-${product.id}`}>
                                              <h4 className="font-semibold mb-4 text-gray-900">Alérgenos y Opciones</h4>
                                              <div className="space-y-4">
                                                {/* Edit Tab Headers */}
                                                <div className="flex border-b border-gray-200">
                                                  <button
                                                    type="button"
                                                    onClick={() => setEditActiveTab('allergens')}
                                                    className={`px-4 py-2 font-medium transition ${editActiveTab === 'allergens' ? 'text-emerald-600 border-b-2 border-emerald-600' : 'text-gray-500 hover:text-gray-700'}`}
                                                  >
                                                    Alérgenos
                                                  </button>
                                                  <button
                                                    type="button"
                                                    onClick={() => setEditActiveTab('options')}
                                                    className={`px-4 py-2 font-medium transition ${editActiveTab === 'options' ? 'text-emerald-600 border-b-2 border-emerald-600' : 'text-gray-500 hover:text-gray-700'}`}
                                                  >
                                                    Opciones
                                                  </button>
                                                </div>

                                                {/* Edit Tab Content */}
                                                <div className="mt-4">
                                                  {editActiveTab === 'allergens' && (
                                                    <AllergenSelector
                                                      selectedAllergens={editingProduct.allergens || []}
                                                      selectedTraces={editingProduct.traces || []}
                                                      onChange={({ allergens, traces }) => 
                                                        setEditingProduct({ ...editingProduct, allergens, traces })
                                                      }
                                                    />
                                                  )}

                                                  {editActiveTab === 'options' && (
                                                    <OptionsSelector
                                                      selectedOptions={editingProduct.options || []}
                                                      onChange={(options) => setEditingProduct({ ...editingProduct, options })}
                                                    />
                                                  )}
                                                </div>
                                              </div>

                                              <div className="flex gap-2 mt-6">
                                                <Button
                                                  data-testid="save-edit-product-btn"
                                                  size="sm"
                                                  onClick={() => updateProduct(product.id, category.id)}
                                                  disabled={savingProduct}
                                                  className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                                                >
                                                  {savingProduct ? (
                                                    <>
                                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                      Guardando...
                                                    </>
                                                  ) : (
                                                    <>
                                                      <Save className="w-4 h-4 mr-2" />
                                                      Guardar
                                                    </>
                                                  )}
                                                </Button>
                                                <Button
                                                  data-testid="cancel-edit-product-btn"
                                                  size="sm"
                                                  variant="outline"
                                                  onClick={() => {
                                                    setEditingProduct(null);
                                                    setEditActiveTab('allergens');
                                                  }}
                                                >
                                                  Cancelar
                                                </Button>
                                              </div>
                                            </div>
                                          ) : (
                                            <InlineProductCard
                                              product={product}
                                              categoryId={category.id}
                                              onSaveFields={saveProductFields}
                                              onImageChange={handleInlineImageUpload}
                                              onToggle={toggleProduct}
                                              onEdit={(p) => { setEditActiveTab('allergens'); setEditingProduct(p); }}
                                              onDelete={deleteProduct}
                                              uploading={inlineUploadingId === product.id}
                                            />
                                          )}
                                        </SortableProduct>
                                      ))}
                                    </div>
                                  </SortableContext>
                                </DndContext>
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        </SortableCategory>
                      ))}
                    </Accordion>
                  </SortableContext>
                </DndContext>
              ) : (
                <p className="text-center text-gray-500 py-8">No hay familias creadas. Añade una familia para comenzar.</p>
              )}
            </div>
          )}

          {/* Promotion Dialog */}
          <Dialog open={showPromotionDialog} onOpenChange={setShowPromotionDialog}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingPromotion ? 'Editar Promoción' : 'Nueva Promoción'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pb-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Título <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="Ej: ¡Nuevo Plato del Día!"
                    value={promotionForm.title}
                    onChange={(e) => setPromotionForm({ ...promotionForm, title: e.target.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1">Campo obligatorio</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Descripción (opcional)</label>
                  <Textarea
                    placeholder="Describe la promoción..."
                    value={promotionForm.description}
                    onChange={(e) => setPromotionForm({ ...promotionForm, description: e.target.value })}
                    rows={3}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Imagen (opcional)</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                    <input
                      ref={promotionImageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handlePromotionImageUpload(e.target.files[0])}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      onClick={() => promotionImageInputRef.current?.click()}
                      disabled={uploadingPromotionImage}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <ImageIcon className="w-4 h-4 mr-2" />
                      {uploadingPromotionImage ? "Subiendo..." : "Subir Imagen"}
                    </Button>
                    {promotionForm.image_url && (
                      <div className="mt-3">
                        <img 
                          src={promotionForm.image_url.startsWith('http') ? promotionForm.image_url : `${BACKEND_URL}${promotionForm.image_url}`} 
                          alt="Preview" 
                          className="max-w-xs max-h-48 mx-auto rounded-lg object-contain" 
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Vincular con Producto (opcional)</label>
                  <select
                    value={promotionForm.product_id}
                    onChange={(e) => handleProductSelect(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">Sin vincular</option>
                    {categories.map((category) => (
                      <optgroup key={category.id} label={category.name}>
                        {(products[category.id] || []).map((product) => (
                          <option key={product.id} value={product.id}>
                            {product.name} - {product.price.toFixed(2)}€
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Al seleccionar un producto, su imagen se cargará automáticamente</p>
                </div>

                {promotionForm.product_id && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Precio Promocional (opcional)</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Precio en oferta"
                      value={promotionForm.promotional_price}
                      onChange={(e) => setPromotionForm({ ...promotionForm, promotional_price: e.target.value })}
                    />
                    <p className="text-xs text-gray-500 mt-1">Deja vacío para usar el precio normal del producto</p>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Fecha de Inicio (opcional)
                    </label>
                    <DatePicker
                      selected={promotionForm.start_date}
                      onChange={(date) => setPromotionForm({ ...promotionForm, start_date: date })}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      dateFormat="dd/MM/yyyy HH:mm"
                      placeholderText="Seleccionar fecha y hora"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      minDate={new Date()}
                    />
                    <p className="text-xs text-gray-500 mt-1">La promoción se mostrará a partir de esta fecha</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Fecha de Fin (opcional)
                    </label>
                    <DatePicker
                      selected={promotionForm.end_date}
                      onChange={(date) => setPromotionForm({ ...promotionForm, end_date: date })}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      dateFormat="dd/MM/yyyy HH:mm"
                      placeholderText="Seleccionar fecha y hora"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      minDate={promotionForm.start_date || new Date()}
                    />
                    <p className="text-xs text-gray-500 mt-1">La promoción dejará de mostrarse después de esta fecha</p>
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button
                    onClick={createOrUpdatePromotion}
                    className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {editingPromotion ? 'Actualizar' : 'Crear'} Promoción
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowPromotionDialog(false);
                      setPromotionForm({ title: "", description: "", image_url: "", product_id: "", promotional_price: "", start_date: null, end_date: null });
                      setEditingPromotion(null);
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* QR Dialog */}
          <Dialog open={showQR} onOpenChange={setShowQR}>
            <DialogContent data-testid="qr-dialog">
              <DialogHeader>
                <DialogTitle>Código QR - {selectedMenu?.name}</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col items-center gap-4">
                {qrImage && <img src={qrImage} alt="QR Code" className="w-64 h-64" data-testid="qr-image" />}
                <p className="text-sm text-gray-600 text-center">
                  Escanea este código QR para ver el menú público
                </p>
                <div className="flex gap-3 w-full">
                  <a
                    href={qrImage}
                    download={`qr-${selectedMenu?.name}.png`}
                    className="flex-1 text-center px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition"
                    data-testid="download-qr-link"
                  >
                    Descargar QR
                  </a>
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(`¡Mira nuestro menú digital! ${window.location.origin}/menu/${selectedMenu?.id}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 text-center px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition flex items-center justify-center gap-2"
                    data-testid="share-whatsapp-link"
                  >
                    <span>📱</span>
                    Compartir
                  </a>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Excel Import Dialog */}
          <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Importar desde Excel</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-blue-900 mb-2">
                    Descarga el archivo de ejemplo para ver el formato correcto
                  </p>
                  <a
                    href={`${API}/download-excel-template`}
                    download="ejemplo_importacion_productos.xlsx"
                    className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-sm"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Descargar Ejemplo Excel
                  </a>
                </div>
                
                <p className="text-sm text-gray-600">
                  El archivo Excel debe tener las siguientes columnas:
                </p>
                <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
                  <li><strong>Categoria</strong>: Nombre de la familia</li>
                  <li><strong>Producto</strong>: Nombre del producto</li>
                  <li><strong>Precio</strong>: Precio en euros</li>
                  <li><strong>Descripcion</strong>: Ingredientes/Descripción</li>
                  <li><strong>Alergenos</strong> (opcional): Separados por comas (ej: gluten,lacteos)</li>
                  <li><strong>Trazas</strong> (opcional): Separados por comas</li>
                  <li><strong>Opciones</strong> (opcional): vegetariano, vegano, picante, muy_picante, nuevo, casero, temporada, local</li>
                </ul>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <input
                    ref={excelInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => handleExcelImport(e.target.files[0])}
                    className="hidden"
                  />
                  <Button
                    onClick={() => excelInputRef.current?.click()}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Seleccionar Archivo Excel
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
            </>
          )}

          {/* Event Dialog */}
          <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingEvent ? 'Editar Evento' : 'Nuevo Evento'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pb-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Título <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="Ej: 🎸 Concierto de Flamenco"
                    value={eventForm.title}
                    onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1">Campo obligatorio</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Descripción (opcional)</label>
                  <Textarea
                    placeholder="Describe el evento..."
                    value={eventForm.description}
                    onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                    rows={3}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Imagen (opcional)</label>
                  <div className="space-y-2">
                    <input
                      ref={eventImageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleEventImageUpload(e.target.files[0])}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => eventImageInputRef.current?.click()}
                      disabled={uploadingEventImage}
                      className="w-full"
                    >
                      {uploadingEventImage ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Subiendo...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          {eventForm.image_url ? 'Cambiar imagen' : 'Subir imagen'}
                        </>
                      )}
                    </Button>
                    {eventForm.image_url && (
                      <div className="relative">
                        <img
                          src={eventForm.image_url.startsWith('http') ? eventForm.image_url : `${BACKEND_URL}${eventForm.image_url}`}
                          alt="Preview"
                          className="w-full h-48 object-cover rounded-lg"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => setEventForm({ ...eventForm, image_url: "" })}
                          className="absolute top-2 right-2"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Fecha de Inicio (opcional)</label>
                    <DatePicker
                      selected={eventForm.start_date}
                      onChange={(date) => setEventForm({ ...eventForm, start_date: date })}
                      showTimeSelect
                      dateFormat="dd/MM/yyyy HH:mm"
                      timeFormat="HH:mm"
                      placeholderText="Seleccionar fecha inicio"
                      className="w-full px-3 py-2 border rounded-lg"
                      locale="es"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Fecha de Fin (opcional)</label>
                    <DatePicker
                      selected={eventForm.end_date}
                      onChange={(date) => setEventForm({ ...eventForm, end_date: date })}
                      showTimeSelect
                      dateFormat="dd/MM/yyyy HH:mm"
                      timeFormat="HH:mm"
                      placeholderText="Seleccionar fecha fin"
                      className="w-full px-3 py-2 border rounded-lg"
                      locale="es"
                    />
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800">
                    💡 <strong>Tip:</strong> Los eventos solo aparecen cuando el cliente pulsa el botón verde con la flamenca 💃
                  </p>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setShowEventDialog(false)}>
                    Cancelar
                  </Button>
                  <Button onClick={saveEvent} className="bg-green-600 hover:bg-green-700">
                    {editingEvent ? 'Guardar Cambios' : 'Crear Evento'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Duplicate Menu Dialog */}
          <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Duplicar Carta</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pb-4">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <p className="text-sm text-purple-800">
                    ℹ️ Se duplicará <strong>{menuToDuplicate?.name}</strong> con todas sus categorías, productos, promociones y eventos.
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Nombre de la nueva carta <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="Ej: Carta Verano 2025"
                    value={duplicateMenuName}
                    onChange={(e) => setDuplicateMenuName(e.target.value)}
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 mt-1">Puedes cambiarlo ahora o editarlo después</p>
                </div>

                {currentUserRole === 'admin' && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Asignar a usuario (opcional)
                    </label>
                    <select
                      value={assignToUser}
                      onChange={(e) => setAssignToUser(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Sin asignar / Mantener original</option>
                      {users.filter(u => u.role === 'owner').map(user => (
                        <option key={user.id} value={user.id}>{user.username}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Selecciona un usuario Owner para copiar la carta a su cuenta
                    </p>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowDuplicateDialog(false);
                      setMenuToDuplicate(null);
                      setDuplicateMenuName("");
                      setAssignToUser("");
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    onClick={duplicateMenu} 
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Duplicar Carta
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* User Dialog */}
          <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Crear Nuevo Usuario</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pb-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Nombre de Usuario <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={newUser.username}
                    onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                    placeholder="Ej: restaurante_centro"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Contraseña <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    placeholder="Contraseña segura"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Rol
                  </label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="owner">Owner (Puede crear cartas)</option>
                    <option value="admin">Admin (Acceso completo)</option>
                  </select>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    💡 Los usuarios Owner pueden crear y gestionar sus propias cartas. Los Admin tienen acceso completo al sistema.
                  </p>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowUserDialog(false);
                      setNewUser({ username: "", password: "", role: "owner" });
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    onClick={createUser}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Crear Usuario
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Users Management Dialog */}
          <Dialog open={showUsersManagementDialog} onOpenChange={setShowUsersManagementDialog}>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Gestión de Usuarios</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-600">Crea y gestiona usuarios que podrán crear sus propias cartas</p>
                  <Button
                    onClick={() => setShowUserDialog(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nuevo Usuario
                  </Button>
                </div>

                {users.length > 0 ? (
                  <div className="space-y-3">
                    {users.map((user) => (
                      <div key={user.id} className="p-4 rounded-lg border-2 border-gray-200 bg-white hover:border-blue-300 transition">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-lg">{user.username}</h3>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                user.role === 'admin' ? 'bg-red-100 text-red-700' :
                                user.role === 'owner' ? 'bg-blue-100 text-blue-700' :
                                'bg-purple-100 text-purple-700'
                              }`}>
                                {user.role === 'admin' ? 'Admin' : 
                                 user.role === 'owner' ? 'Owner' : 
                                 'Presidente'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                              Creado: {new Date(user.created_at).toLocaleDateString('es-ES')}
                            </p>
                            {user.menu_id && (
                              <p className="text-xs text-gray-400 mt-1">
                                Asignado a carta ID: {user.menu_id}
                              </p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteUser(user.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <div className="text-4xl mb-3">👥</div>
                    <p>No hay usuarios creados. Crea uno para empezar.</p>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Change Password Dialog */}
          <ChangePassword 
            isOpen={showChangePassword} 
            onClose={() => setShowChangePassword(false)} 
          />
        </div>
      </div>
    </div>
  );
}